import React, { createContext, useContext, useState, useEffect } from 'react';
import { User as AppUser, UnitType } from '../types';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

interface SavedSession {
  user: AppUser;
  accessToken: string;
  refreshToken: string;
}

interface AuthContextType {
  user: AppUser | null;
  departmentProfiles: AppUser[];
  savedSessions: SavedSession[];
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, userData: any) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<AppUser>) => void;
  switchAccount: (userId: string) => Promise<void>;
  switchDepartmentProfile: (profileId: string) => void;
  addAccount: () => Promise<void>;
  removeAccount: (userId: string) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock users for demo (will be replaced with Supabase data)
export const mockUsers: AppUser[] = [
  {
    id: 'bcd9f0f7-bd4d-4512-9d83-2d3cafcf5bbc',
    name: 'Ceylan Tuncil',
    email: 'ceylan@pisaedu.com',
    role: 'unit',
    unitType: 'sales',
    branchId: '126e8dc2-b4af-42a4-ac78-ab5aecc9ac6a',
    unitId: '701bea24-3aa1-43e1-b2b0-27916f0586b3',
    isActive: true,
    createdAt: new Date(),
    permissions: ['view_leads', 'manage_campaigns', 'view_analytics']
  },
  {
    id: 'ceylan02-0000-0000-0000-000000000002',
    name: 'Ceylan Tuncil',
    email: 'ceylan@pisaedu.com',
    role: 'unit',
    unitType: 'education_consultant',
    branchId: '126e8dc2-b4af-42a4-ac78-ab5aecc9ac6a',
    unitId: '701bea24-3aa1-43e1-b2b0-27916f0586b3',
    isActive: true,
    createdAt: new Date(),
    permissions: ['view_students', 'edit_students', 'add_notes', 'schedule_meetings']
  }
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [departmentProfiles, setDepartmentProfiles] = useState<AppUser[]>([]);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);

  // Load saved sessions from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('pisa_saved_sessions');
    if (stored) {
      try {
        setSavedSessions(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse saved sessions', e);
      }
    }
  }, []);

  // Save sessions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pisa_saved_sessions', JSON.stringify(savedSessions));
  }, [savedSessions]);

  // Sync sessions across tabs using storage event
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'pisa_saved_sessions' && e.newValue) {
        try {
          const syncedSessions = JSON.parse(e.newValue);
          setSavedSessions(syncedSessions);
        } catch (err) {
          console.error('Failed to sync sessions from storage event', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`[AUTH] Auth event: ${event}`, session?.user?.id);
        setSupabaseUser(session?.user ?? null);

        if (session?.user) {
          // Update saved sessions immediately with the new token
          if (session.access_token && session.refresh_token) {
            setSavedSessions(prev => {
              // Check if we already have this user in our saved sessions
              const existingSessionIndex = prev.findIndex(s => s.user.id === session.user.id);

              if (existingSessionIndex >= 0) {
                // User exists, update their tokens
                const newSessions = [...prev];
                newSessions[existingSessionIndex] = {
                  ...newSessions[existingSessionIndex],
                  accessToken: session.access_token,
                  refreshToken: session.refresh_token
                };
                return newSessions;
              }

              // If user acts as a new login (via fetchProfile or login), they will be added there.
              // Here we strictly update EXISTING sessions to capture token refreshes.
              return prev;
            });
          }

          // Only fetch profile if we don't have it or if it's a different user (e.g. SIGNED_IN)
          // Avoid fetching on TOKEN_REFRESHED if we already have the user loaded
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || !user || user.id !== session.user.id) {
            fetchProfile(session.user.id);
          }
        } else {
          // If signed out, we don't remove from savedSessions here (handled by explicit actions)
          // Just clear current state
          setUser(null);
          setDepartmentProfiles([]);
          setIsLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      if (!supabase) {
        throw new Error('Supabase bağlantısı bulunamadı');
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId);

      if (error) {
        throw new Error(`Profil yüklenirken hata: ${error.message}`);
      }

      if (!data || data.length === 0) {
        console.log('No profile found for user:', userId);
        setUser(null);
        setDepartmentProfiles([]);
        return;
      }

      const profileData = data[0];

      if (profileData.is_active === false) {
        console.warn('[AUTH] Frozen account session rejected:', userId);
        setUser(null);
        setSupabaseUser(null);
        setDepartmentProfiles([]);
        setSavedSessions(prev => prev.filter(session => session.user.id !== userId));
        await supabase.auth.signOut({ scope: 'local' });
        return;
      }

      // Convert Supabase profile to app user format
      const appUser: AppUser = {
        id: profileData.id,
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        role: profileData.role,
        avatar: profileData.avatar,
        branchId: profileData.branch_id,
        unitId: profileData.unit_id,
        unitType: profileData.unit_type,
        isActive: profileData.is_active,
        permissions: profileData.permissions,
        responsibleUniversities: profileData.responsible_universities,
        createdAt: new Date(profileData.created_at),
        lastLogin: profileData.last_login ? new Date(profileData.last_login) : undefined,
        karelToken: profileData.karel_token || null
      };

      // Fetch all department profiles associated with this email
      let deptProfiles: AppUser[] = [appUser];
      if (profileData.email) {
        const { data: allProfilesData } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', profileData.email)
          .eq('is_active', true);

        if (allProfilesData && allProfilesData.length > 0) {
          deptProfiles = allProfilesData.map(p => ({
            id: p.id,
            name: p.name,
            email: p.email,
            phone: p.phone,
            role: p.role,
            avatar: p.avatar,
            branchId: p.branch_id,
            unitId: p.unit_id,
            unitType: p.unit_type,
            isActive: p.is_active,
            permissions: p.permissions,
            responsibleUniversities: p.responsible_universities,
            createdAt: new Date(p.created_at),
            lastLogin: p.last_login ? new Date(p.last_login) : undefined,
            karelToken: p.karel_token || null
          }));
        }
      }

      // Check assigned_departments in preferences JSONB
      const prefDepts: string[] = profileData.preferences?.assigned_departments || [];

      // Special fallback for Ceylan Tuncil or configured multi-role users
      if (profileData.email === 'ceylan@pisaedu.com') {
        if (!prefDepts.includes('sales')) prefDepts.push('sales');
        if (!prefDepts.includes('education_consultant')) prefDepts.push('education_consultant');
      }

      // Merge mockUsers matching email if present
      const matchingMockUsers = mockUsers.filter(mu => mu.email === profileData.email);
      for (const mu of matchingMockUsers) {
        if (!deptProfiles.some(p => p.unitType === mu.unitType)) {
          deptProfiles.push(mu);
        }
      }

      // Merge prefDepts into deptProfiles as virtual profiles if not already present
      for (const deptType of prefDepts) {
        if (!deptProfiles.some(p => p.unitType === deptType)) {
          deptProfiles.push({
            ...appUser,
            id: `${appUser.id}_${deptType}`,
            unitType: deptType as UnitType
          });
        }
      }

      setDepartmentProfiles(deptProfiles);

      // Check if user previously selected a specific department profile for this email
      const activeDeptProfileId = localStorage.getItem(`pisa_active_profile_${profileData.email}`);
      if (activeDeptProfileId) {
        const selectedDeptProfile = deptProfiles.find(p => p.id === activeDeptProfileId);
        if (selectedDeptProfile) {
          setUser(selectedDeptProfile);
        } else {
          setUser(appUser);
        }
      } else {
        setUser(appUser);
      }

      // Update/Add to saved sessions
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session) {
        setSavedSessions(prev => {
          const filtered = prev.filter(s => s.user.id !== appUser.id);
          return [...filtered, {
            user: appUser,
            accessToken: sessionData.session!.access_token,
            refreshToken: sessionData.session!.refresh_token
          }];
        });
      }

    } catch (error) {
      console.error('Error fetching profile:', error);
      setUser(null);
      setDepartmentProfiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      console.log('[AUTH] Login attempt for:', normalizedEmail);

      if (!supabase) {
        console.error('[AUTH] Supabase client not available');
        throw new Error('Supabase bağlantısı bulunamadı');
      }

      // Supabase authentication
      console.log('[AUTH] Attempting Supabase sign in...');
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password
      });

      if (error) {
        console.error('[AUTH] Supabase auth error:', error);
        throw new Error(error.message);
      }

      if (!data.user) {
        console.error('[AUTH] No user data returned');
        throw new Error('Giriş başarısız');
      }

      console.log('[AUTH] Successfully authenticated user:', data.user.id);

      const { data: userData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (profileError) {
        console.error('[AUTH] Failed to fetch profile:', profileError);
        await supabase.auth.signOut({ scope: 'local' });
        throw new Error('Kullanıcı profili bulunamadı');
      }

      if (userData.is_active === false) {
        console.warn('[AUTH] Frozen account login rejected:', data.user.id);
        await supabase.auth.signOut({ scope: 'local' });
        setUser(null);
        setSupabaseUser(null);
        setDepartmentProfiles([]);
        setSavedSessions(prev => prev.filter(session => session.user.id !== data.user!.id));
        throw new Error('Bu hesap yönetici tarafından dondurulmuştur');
      }

      console.log('[AUTH] Profile loaded successfully');

      // Update last login only after the account status has been verified.
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.user.id);

      if (updateError) {
        console.warn('[AUTH] Failed to update last_login:', updateError);
      }

      // Explicitly fetch user profile and update saved sessions to ensure fresh token is stored
      // This bypasses reliance on onAuthStateChange for the initial storage
      await fetchProfile(data.user.id);

      /* Disable Karel userLoggedIn event for now
      if (userData && userData.karel_token) {
        console.log('[AUTH] Dispatching userLoggedIn event for Karel');
        window.dispatchEvent(new CustomEvent('userLoggedIn', {
          detail: {
            ...userData,
            karelToken: userData.karel_token
          }
        }));
      }
      */

      setIsLoading(false);
      console.log('[AUTH] Login successful');
      return true;
    } catch (error: any) {
      console.error('[AUTH] Login error:', error);
      console.error('[AUTH] Error message:', error?.message);

      setIsLoading(false);
      return false;
    }
  };

  const register = async (email: string, password: string, userData: any): Promise<boolean> => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });

      if (error) throw error;

      // Create profile
      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([{
            id: data.user.id,
            email,
            ...userData
          }]);

        if (profileError) throw profileError;
      }

      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = async () => {
    if (user) {
      setSavedSessions(prev => prev.filter(s => s.user.id !== user.id));
    }

    await supabase.auth.signOut();
    setUser(null);
    setSupabaseUser(null);
    localStorage.removeItem('pisaUser');
    localStorage.removeItem('activeTab');

    // Karel WebPhone'u durdur
    try {
      if (window.stopKarelWebPhone) {
        window.stopKarelWebPhone();
        console.log('[Karel] WebPhone stopped on logout');
      }
    } catch (err) {
      console.error('Karel durdurma hatası:', err);
    }
  };

  // Ref to prevent double-invocation of switchAccount
  const isSwitchingRef = React.useRef(false);

  const switchAccount = async (userId: string) => {
    // Prevent multiple calls
    if (isLoading || isSwitchingRef.current) return;

    const sessionToSwitch = savedSessions.find(s => s.user.id === userId);
    if (!sessionToSwitch) return;

    // Keep track of current session to restore if switch fails
    const currentUser = user;
    const currentSession = savedSessions.find(s => s.user.id === currentUser?.id);

    setIsLoading(true);
    isSwitchingRef.current = true;

    try {
      console.log("[AUTH] Switching to account:", userId);

      // We use refreshSession instead of setSession to ensure we get a FRESH pair of tokens from the server.
      // This validates the refresh token immediately and rotates it.
      // refreshSession automatically sets the session in the client if successful.
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: sessionToSwitch.refreshToken
      });

      if (error) {
        throw error;
      }

      if (data.session) {
        console.log("[AUTH] Switch successful, session refreshed");

        // Update saved sessions immediately with new tokens
        setSavedSessions(prev => {
          const index = prev.findIndex(s => s.user.id === userId);
          const newSessions = [...prev];

          // If user exists, update. If not (shouldn't happen), append.
          if (index >= 0) {
            newSessions[index] = {
              ...newSessions[index],
              accessToken: data.session!.access_token,
              refreshToken: data.session!.refresh_token
            };
          } else {
            // Should already be there, but just in case
            newSessions.push({
              user: sessionToSwitch.user, // Use the stored user info
              accessToken: data.session!.access_token,
              refreshToken: data.session!.refresh_token
            });
          }
          return newSessions;
        });
      }

      // Explicitly fetch profile to ensure UI updates
      await fetchProfile(userId);

    } catch (e: any) {
      console.error("[AUTH] Switch account failed:", e);

      let errorMessage = e?.message || 'Bilinmeyen Hata';
      const isTokenError = errorMessage.includes('Invalid Refresh Token') ||
        errorMessage.includes('Refresh Token Not Found') ||
        errorMessage.includes('refresh_token_not_found');

      if (isTokenError) {
        console.log("[AUTH] Token invalid, removing account from list:", userId);
        // Remove the invalid account so the user knows they must re-login
        removeAccount(userId);
        errorMessage = 'Oturum süresi doldu ve güvenlik nedeniyle hesap listeden kaldırıldı. Lütfen "+ Hesap Ekle" diyerek tekrar giriş yapın.';
      } else {
        // Try to restore previous session if valid and not a token error (network issue etc)
        if (currentUser && currentSession) {
          console.log("[AUTH] Restoring previous session...");
          try {
            await supabase.auth.setSession({
              access_token: currentSession.accessToken,
              refresh_token: currentSession.refreshToken
            });
          } catch (restoreError) {
            console.error("[AUTH] Failed to restore previous session:", restoreError);
          }
        }
      }

      setIsLoading(false);
      alert(`Giriş yapılamadı: ${errorMessage}`);
    } finally {
      isSwitchingRef.current = false;
    }
  };

  const addAccount = async () => {
    // Just clear current session locally without notifying server to revoke token
    // This is crucial for multi-account support so we don't kill other sessions
    // Explicitly set user to null to force UI to login screen immediately
    setUser(null);
    setSupabaseUser(null);

    // We used to call signOut({ scope: 'local' }) here, but it might be interfering with
    // the persistence of the session or causing edge cases with tokens.
    // Since we are about to login with a new user (which overwrites the supabase local storage key),
    // we can skip the explicit sign out.
    // await supabase.auth.signOut({ scope: 'local' });

    // Clear the specific keys Supabase uses just to be sure we start fresh for the NEW login
    // but without calling the SDK method that might have side effects.
    // Actually, asking the user to login again will handle the overwrite naturally.
  };

  const removeAccount = (userId: string) => {
    setSavedSessions(prev => prev.filter(s => s.user.id !== userId));
    // If removing current user, logout
    if (user?.id === userId) {
      logout();
    }
  };

  const updateUser = async (userData: Partial<AppUser>) => {
    if (user) {
      if (supabaseUser) {
        // Update in Supabase
        try {
          const { data, error } = await supabase
            .from('profiles')
            .update({
              name: userData.name,
              phone: userData.phone,
              avatar: userData.avatar,
              updated_at: new Date().toISOString()
            })
            .eq('id', supabaseUser.id)
            .select()
            .single();

          if (error) throw error;

          const updatedUser = { ...user, ...userData };
          setUser(updatedUser);
        } catch (error) {
          console.error('Error updating profile:', error);
        }
      } else {
        // Fallback to localStorage for demo
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        localStorage.setItem('pisaUser', JSON.stringify(updatedUser));
      }
    }
  };

  const switchDepartmentProfile = (profileId: string) => {
    const target = departmentProfiles.find(p => p.id === profileId);
    if (target) {
      setUser(target);
      if (target.email) {
        localStorage.setItem(`pisa_active_profile_${target.email}`, profileId);
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      departmentProfiles,
      savedSessions,
      login,
      register,
      logout,
      updateUser,
      switchAccount,
      switchDepartmentProfile,
      addAccount,
      removeAccount,
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
